import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api/client'

/**
 * usePendingChangeSignal (ADR-122 D8) — "is anything waiting to go live?", as a
 * SIGNAL rather than a number.
 *
 * D8 retires the ADR-114 D2 banner, which answered this question with a
 * repo-wide commit count rendered inside one agent's tab. Two things were wrong
 * with that and both are fixed by where this gets used, not just by what it
 * returns: the object was a count (merge commits and automated platform syncs
 * included — orion read "4 changes" for two real ones), and the place was one
 * agent's page for a fleet-wide fact.
 *
 * So this deliberately exposes NO count. The named list lives on Updates, which
 * is the one surface that answers the question properly; everything else gets a
 * boolean it can render as a dot or a sentence.
 *
 * `unknown` is separate from `waiting: false` on purpose. A failed read is not
 * an all-clear — that substitution is the founding incident (three activities
 * deleted, the deletion staged, the page reading the deployed branch, and they
 * kept running for two days). Callers that cannot express uncertainty should
 * render nothing when `unknown`, and leave the authoritative answer to Updates,
 * which says outright that it could not check.
 */
export interface PendingChangeSignal {
  /** True only when the read SUCCEEDED and named at least one operator change. */
  waiting: boolean
  /** A canonical platform reconcile is pending — a platform condition, not
   * operator work (D8). Never rendered as a count or attributed to an agent. */
  platformSyncPending: boolean
  /** The read failed, or has not resolved yet. NOT the same as "nothing". */
  unknown: boolean
}

export const PENDING_CHANGES_EVENT = 'mc:pending-changes-changed'

/** Tell every mounted signal to re-read — after Take live, or a promotion phase change. */
export function notifyPendingChangesChanged(): void {
  window.dispatchEvent(new Event(PENDING_CHANGES_EVENT))
}

export function usePendingChangeSignal(refreshKey?: string): PendingChangeSignal {
  const [signal, setSignal] = useState<PendingChangeSignal>({
    waiting: false, platformSyncPending: false, unknown: true,
  })
  const request = useRef(0)

  const read = useCallback(async () => {
    const current = ++request.current
    try {
      const s = await api.getPendingChanges()
      if (current !== request.current) return
        // A fleet with no GitHub integration is `unknown`, not "nothing
        // waiting" — there is genuinely nothing we can say about it.
        if (!s.configured) {
          setSignal({ waiting: false, platformSyncPending: false, unknown: true })
          return
        }
        setSignal({
          waiting: (s.changes?.length ?? 0) > 0,
          platformSyncPending: (s.platform_syncs?.length ?? 0) > 0,
          unknown: false,
        })
    } catch {
      // Includes the 403 a role below operator gets. Stay `unknown` rather
      // than reporting an all-clear we did not earn.
      if (current === request.current) setSignal({ waiting: false, platformSyncPending: false, unknown: true })
    }
  }, [])

  // One read on mount, then one for each route change.
  useEffect(() => { void read() }, [refreshKey, read])

  // Refreshes preserve the last signal while the new read is in flight.
  useEffect(() => {
    const refresh = () => { void read() }
    window.addEventListener('focus', refresh)
    window.addEventListener(PENDING_CHANGES_EVENT, refresh)
    return () => {
      window.removeEventListener('focus', refresh)
      window.removeEventListener(PENDING_CHANGES_EVENT, refresh)
    }
  }, [read])

  return signal
}
